import { useTranslation } from 'react-i18next';

/**
 * Copy for the contact-photo flow.
 *
 * Kept here rather than in en.json/fr.json on purpose: i18next's fallbackLng
 * is 'fr' (Loi 101), so an EN-only key would render as a raw key on a French
 * dashboard, and fr.json is frozen (see .claude/rules/fr-freeze-pin.md).
 * A string with no French falls back to English.
 *
 * NEVER invent French here. FR copy comes from Normand, verbatim.
 * Record: docs/plans/2026-07-29-fr-items-for-normand.md.
 */
const en = {
  addPhoto: 'Add photo',
  changePhoto: 'Change photo',
  sheetTitle: 'Contact photo',
  sheetHint: 'This is how the button will look on the phone.',
  uploadFromDevice: 'Upload from device',
  takePicture: 'Take a picture',
  removePhoto: 'Remove photo',
  done: 'Done',
  close: 'Close',
  opening: 'Loading photo…',
  cropTitle: 'Fit the face in the outline',
  cropHint: 'Drag the photo and zoom until the face fills the outline.',
  zoom: 'Zoom',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  previewLabel: 'On the phone',
  usePhoto: 'Use this photo',
  cancel: 'Cancel',
  saving: 'Saving photo…',
  tryAgain: 'Try again',
  photoOf: 'Photo of {{name}}',
  noPhoto: 'No photo yet',
  noName: 'Name',
  unsupportedType: 'This type of file cannot be used. Please choose a JPG, PNG, WebP or iPhone (HEIC) photo.',
  tooLarge: 'This photo is too large (over 30 MB). Please choose a smaller photo.',
  cannotOpen: 'This photo could not be opened. Please choose another photo.',
  heicUnsupported:
    'This iPhone photo (HEIC) cannot be opened on this device. Please add it from an iPhone or a Mac, or choose a JPG or PNG photo.',
  tooZoomed: 'Zoomed in too far: the face would look blurry on the phone. Please zoom out a little.',
  tooSmall: 'This photo is too small to look sharp on the phone. Please choose a larger photo.',
  uploadFailed: 'The photo could not be saved. Please check your internet connection and try again.',
  uploadRefused: 'Our server could not accept this photo. Please contact AidFone support.',
  signupPhotosNotSaved:
    'Some contact photos could not be saved. Your account is fine. You can add the photos later in your dashboard, under Contacts.',
} as const;

export type ContactPhotoTextKey = keyof typeof en;

// Normand-supplied translations only, VERBATIM (native review, final):
// supplied in chat 2026-10-03 as P1–P32. Do not correct, re-punctuate or
// "normalise" them. Typography revised by Normand afterwards, words unchanged:
// P10 ends with "…" (U+2026) like P19, and P28 has a non-breaking space
// (U+00A0, written   so it stays visible) before its colon.
// Missing keys fall back to English.
const fr: Partial<Record<ContactPhotoTextKey, string>> = {
  addPhoto: 'Ajouter une photo', // P1
  changePhoto: 'Changer la photo', // P2
  sheetTitle: 'Photo du contact', // P3
  sheetHint: 'Voici à quoi ressemblera le bouton sur le téléphone.', // P4
  uploadFromDevice: "Téléverser depuis l'appareil", // P5
  takePicture: 'Prendre une photo', // P6
  removePhoto: 'Retirer la photo', // P7
  done: 'Terminé', // P8
  close: 'Fermer', // P9
  opening: 'Chargement de la photo…', // P10
  cropTitle: 'Placez le visage dans le cadre', // P11
  cropHint: "Déplacez la photo et zoomez jusqu'à ce que le visage remplisse le cadre.", // P12
  zoom: 'Zoom', // P13
  zoomIn: 'Zoom avant', // P14
  zoomOut: 'Zoom arrière', // P15
  previewLabel: 'Sur le téléphone', // P16
  usePhoto: 'Utiliser cette photo', // P17
  cancel: 'Annuler', // P18
  saving: 'Enregistrement de la photo…', // P19
  tryAgain: 'Réessayer', // P20
  photoOf: 'Photo de {{name}}', // P21
  noPhoto: 'Aucune photo pour le moment', // P22
  noName: 'Nom', // P23
  unsupportedType: 'Ce type de fichier ne peut pas être utilisé. Veuillez choisir une photo JPG, PNG, WebP ou iPhone (HEIC).', // P24
  tooLarge: 'Cette photo est trop volumineuse (plus de 30 Mo). Veuillez en choisir une plus petite.', // P25
  cannotOpen: "Cette photo n'a pas pu être ouverte. Veuillez en choisir une autre.", // P26
  heicUnsupported:
    "Cette photo iPhone (HEIC) ne peut pas être ouverte sur cet appareil. Veuillez l'ajouter depuis un iPhone ou un Mac, ou choisir une photo JPG ou PNG.", // P27
  tooZoomed: 'Trop agrandi : le visage serait flou sur le téléphone. Veuillez réduire un peu.', // P28
  tooSmall: 'Cette photo est trop petite pour être nette sur le téléphone. Veuillez en choisir une plus grande.', // P29
  uploadFailed: "La photo n'a pas pu être enregistrée. Veuillez vérifier votre connexion internet et réessayer.", // P30
  uploadRefused: "Le serveur n'a pas pu accepter cette photo. SVP contacter le support AidFone.", // P31
  signupPhotosNotSaved:
    "Certaines photos de vos contacts n'ont pas pu être enregistrées. Votre compte est en ordre. Vous pourrez ajouter les photos plus tard dans votre tableau de bord, sous Contacts.", // P32
};

const byLanguage: Record<string, Partial<Record<ContactPhotoTextKey, string>>> = { en, fr };

/** Keys with no translation in `language` (they show in English). */
export function untranslatedContactPhotoKeys(language: string): ContactPhotoTextKey[] {
  const dict = byLanguage[language.split('-')[0]];
  return (Object.keys(en) as ContactPhotoTextKey[]).filter((k) => dict?.[k] === undefined);
}

export function contactPhotoText(
  language: string | undefined,
  key: ContactPhotoTextKey,
  vars?: Record<string, string>,
): string {
  const lang = (language || 'en').split('-')[0];
  const raw = byLanguage[lang]?.[key] ?? en[key];
  return vars ? raw.replace(/\{\{(\w+)\}\}/g, (_, name: string) => vars[name] ?? '') : raw;
}

export function useContactPhotoText() {
  const { i18n } = useTranslation();
  return (key: ContactPhotoTextKey, vars?: Record<string, string>) => contactPhotoText(i18n.language, key, vars);
}
